import { gsap, ScrollTrigger, prefersReducedMotion } from './motion';

const appearSelectors = [
	'.zero-un-hero__content:not(.zero-un-hero__content--slideshow) .zero-un-hero__content-inner > *',
	'.zero-un-hero__content-panel.is-active .zero-un-hero__content-inner > *',
	'.zero-un-hero-boxes__item',
	'.zero-un-hero-floating-card',
	'.zero-un-hero-form-panel',
	'.zero-un-hero__split-media',
	'.zero-un-hero__side-media-frame',
	'.zero-un-intro__content-inner > *',
	'.zero-un-intro__media',
	'.zero-un-contents__main-content > *',
	'.zero-un-contents__content > *',
	'.zero-un-contents-row',
	'.zero-un-contents-card',
	'.zero-un-contents__main-media-wrap',
	'.zero-un-contents__cards-heading > *',
	'.zero-un-contents__split-heading',
	'.zero-un-contents__split-body > *',
	'.zero-un-list__content > *',
	'.zero-un-list__main-content > *',
	'.zero-un-list-item',
	'.zero-un-list__media-wrap',
	'.zero-un-list__panel',
	'.zero-un-accordion__intro > *',
	'.zero-un-accordion-item',
	'.zero-un-media',
	'.zero-un-cta__content-inner > *',
	'.zero-un-cta__media',
	'.zero-un-cta-card',
	'.zero-un-contact__content > *',
	'.zero-un-contact__media',
	'.zero-un-contact__form-wrap',
	'.zero-un-newsletter__media',
	'.zero-un-newsletter__body',
	'.zero-un-stats__intro-inner > *',
	'.zero-un-stats-item',
	'.zero-un-testimonials__intro > *',
	'.zero-un-testimonial-card',
	'.zero-un-testimonials__media-card',
	'.zero-un-testimonials__controls',
	'.zero-un-logos__intro > *',
	'.zero-un-logos-card',
	'.zero-un-logos-cta',
	'.zero-un-timeline__intro > *',
	'.zero-un-timeline__main-media',
	'.zero-un-timeline-item',
	'.zero-un-timeline__controls',
	'.zero-un-ticketing__intro > *',
	'.zero-un-ticketing__filters',
	'.zero-un-ticketing-event',
	'.zero-un-sidebars__sidebar-block',
	'.zero-un-sidebars__content > *',
	'.zero-un-cards__intro > *',
	'.zero-un-cards__head-actions',
	'.zero-un-cards-calendar',
	'.zero-un-cards-card',
].join(',');

const excludedSelectors = [
	'[hidden]',
	'.screen-reader-text',
	'.zero-un-cards-gallery-popup',
	'.zero-un-testimonials__centered-sizer',
	'.zero-un-logos__marquee-group[aria-hidden="true"]',
	'.zero-un-hero__content-panel:not(.is-active)',
].join(',');

const routeKey = (route = null) => {
	return route?.key || `${route?.path || route?.pathname || ''}${route?.search || ''}`;
};

const uniqueElements = (items = []) => {
	return [...new Set(items.filter(Boolean))];
};

const getAppearRoot = () => {
	if(typeof document === 'undefined'){
		return null;
	}

	return document.querySelector('.default-page');
};

const isElementVisibleEnough = (element) => {
	if(!(element instanceof HTMLElement)){
		return false;
	}

	if(element.closest(excludedSelectors)){
		return false;
	}

	if(element.closest('[aria-hidden="true"]')){
		return false;
	}

	if(element.matches('[aria-hidden="true"]')){
		return false;
	}

	return true;
};

const collectAppearElements = (root = getAppearRoot()) => {
	if(!root){
		return [];
	}

	return uniqueElements(Array.from(root.querySelectorAll(appearSelectors)))
		.filter(isElementVisibleEnough);
};

export const appearAnimations = {
	key: null,
	elements: [],
	triggers: [],
	tweens: [],
	prepared: false,
	clearElements(elements = this.elements){
		if(!elements.length || !gsap){
			return;
		}

		gsap.set(elements, {
			clearProps: 'opacity,visibility,transform,willChange',
		});
	},
	kill({ clear = true } = {}){
		this.triggers.forEach((trigger) => trigger?.kill?.());
		this.triggers = [];

		this.tweens.forEach((tween) => tween?.kill?.());
		this.tweens = [];

		if(this.elements.length){
			gsap?.killTweensOf?.(this.elements);

			if(clear){
				this.clearElements(this.elements);
			}
		}

		this.elements.forEach((element) => {
			delete element.dataset.zeroUnAppearPrepared;
			delete element.dataset.zeroUnAppeared;
		});

		this.elements = [];
		this.prepared = false;
		this.key = null;
	},
	prepare(route = null){
		this.kill();
		this.key = routeKey(route);
		this.elements = collectAppearElements();

		if(prefersReducedMotion || !this.elements.length){
			this.prepared = true;
			return this.elements;
		}

		this.elements.forEach((element) => {
			element.dataset.zeroUnAppearPrepared = 'true';
			delete element.dataset.zeroUnAppeared;
		});

		gsap.set(this.elements, {
			autoAlpha: 0,
			y: 28,
			willChange: 'transform, opacity',
		});

		this.prepared = true;

		return this.elements;
	},
	animate(batch = []){
		const elements = uniqueElements(batch)
			.filter((element) => element?.dataset?.zeroUnAppearPrepared === 'true')
			.filter((element) => element.dataset.zeroUnAppeared !== 'true');

		if(!elements.length){
			return null;
		}

		elements.forEach((element) => {
			element.dataset.zeroUnAppeared = 'true';
		});

		const tween = gsap.to(elements, {
			autoAlpha: 1,
			y: 0,
			duration: 0.78,
			ease: 'power3.out',
			stagger: {
				each: 0.055,
				from: 'start',
			},
			overwrite: 'auto',
			onComplete: () => {
				this.clearElements(elements);
				elements.forEach((element) => {
					delete element.dataset.zeroUnAppearPrepared;
				});
			},
		});

		this.tweens.push(tween);

		return tween;
	},
	play(route = null){
		const nextKey = routeKey(route);

		if(nextKey && this.key !== nextKey){
			this.prepare(route);
		}

		if(!this.prepared){
			this.prepare(route);
		}

		if(typeof document === 'undefined'){
			return;
		}

		const elements = this.elements
			.filter((element) => document.documentElement.contains(element))
			.filter((element) => element?.dataset?.zeroUnAppeared !== 'true');

		if(!elements.length){
			return;
		}

		if(prefersReducedMotion || !ScrollTrigger){
			this.clearElements(elements);
			this.kill({ clear: false });
			return;
		}

		gsap.registerPlugin(ScrollTrigger);

		this.triggers = ScrollTrigger.batch(elements, {
			start: 'top 88%',
			once: true,
			batchMax: 12,
			interval: 0.08,
			onEnter: (batch) => this.animate(batch),
		});

		window.requestAnimationFrame(() => {
			window.gscroll?.refresh?.();
			ScrollTrigger.refresh();
		});
	},
};
